import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, ChefHat, Star, AlertCircle, Loader, Search, Filter, MapPin, DollarSign, MessageCircle, Share2, Copy, Check, Utensils, Beer, ListRestart as Restaurant, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { useError } from '../contexts/ErrorContext';
import BackButton from '../components/BackButton';
import LocationSearch from '../components/LocationSearch';
import LoadingIndicator from '../components/LoadingIndicator';
import UserSearchModal from '../components/UserSearchModal';
import { createChatRoom, sendMessage } from '../lib/chat';
import { createListing } from '../lib/listings';
import DateInput from '../components/DateInput';

const CreateListing = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { setError } = useError();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [bars, setBars] = useState<any[]>([]);
  const [filteredVenues, setFilteredVenues] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVenue, setSelectedVenue] = useState<any | null>(null);
  const [generatedListing, setGeneratedListing] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ address: string; lat: number; lng: number; } | null>(null);
  const [copied, setCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    partySize: 2,
    venueType: 'restaurant' as 'restaurant' | 'bar',
    location: '',
    venueId: '',
    preferences: '',
    description: ''
  });

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    // Filter venues based on search query and venue type
    if (restaurants.length > 0 || bars.length > 0) {
      const venues = formData.venueType === 'restaurant' ? restaurants : bars;
      
      if (searchQuery.trim() === '') {
        setFilteredVenues(venues);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = venues.filter(venue => 
          venue.name.toLowerCase().includes(query) || 
          (venue.cuisine_type && venue.cuisine_type.toLowerCase().includes(query)) ||
          (venue.bar_type && venue.bar_type.toLowerCase().includes(query)) ||
          (venue.address && venue.address.toLowerCase().includes(query))
        );
        setFilteredVenues(filtered);
      }
    }
  }, [searchQuery, formData.venueType, restaurants, bars]);

  const initialize = async () => {
    try {
      setLoading(true);
      
      // Check authentication status
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError) {
        console.error("Auth error:", authError);
        setIsAuthenticated(false);
      } else {
        setIsAuthenticated(!!session);
      }

      if (session?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('location_lat, location_lng, location')
          .eq('id', session.user.id)
          .single();

        if (profileError) {
          console.warn("Error fetching profile:", profileError);
        } else if (profile?.location_lat && profile?.location_lng) {
          setSelectedLocation({
            address: profile.location || '',
            lat: profile.location_lat,
            lng: profile.location_lng
          });
          setFormData(prev => ({ ...prev, location: profile.location || '' }));
        }
      }

      // Fetch restaurants and bars
      await Promise.all([
        fetchRestaurants(),
        fetchBars()
      ]);
    } catch (err) {
      console.error('Error initializing:', err);
      setError(new Error('Failed to initialize page'), 'INIT_ERROR');
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('status', 'approved')
        .order('name');

      if (error) throw error;
      setRestaurants(data || []);
      if (formData.venueType === 'restaurant') {
        setFilteredVenues(data || []);
      }
    } catch (err) {
      console.error('Error fetching restaurants:', err);
      // Don't set error here to avoid blocking the page
    }
  };

  const fetchBars = async () => {
    try {
      const { data, error } = await supabase
        .from('bars')
        .select('*')
        .eq('status', 'approved')
        .order('name');

      if (error) throw error;
      setBars(data || []);
      if (formData.venueType === 'bar') {
        setFilteredVenues(data || []);
      }
    } catch (err) {
      console.error('Error fetching bars:', err);
      // Don't set error here to avoid blocking the page
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // If venue type changes, update filtered venues
    if (name === 'venueType') {
      setSelectedVenue(null);
      setFormData(prev => ({
        ...prev,
        venueId: ''
      }));
      setFilteredVenues(value === 'restaurant' ? restaurants : bars);
    }
  };

  const handleVenueSelect = (venue: any) => {
    setSelectedVenue(venue);
    setFormData(prev => ({
      ...prev,
      venueId: venue.id
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleLocationSelect = (location: { address: string; lat: number; lng: number }) => {
    setSelectedLocation(location);
    setFormData(prev => ({ ...prev, location: location.address }));
  };

  const handleDateChange = (date: string) => {
    setFormData(prev => ({ ...prev, date }));
  };

  const generateListing = () => {
    setSuccessMessage(null);

    if (!selectedVenue) {
      setError(new Error('Bitte wähle zuerst eine Location'), 'VALIDATION_ERROR');
      return;
    }

    if (!formData.date) {
      setError(new Error('Bitte wähle zuerst ein Datum'), 'VALIDATION_ERROR');
      return;
    }

    if (!formData.time) {
      setError(new Error('Bitte wähle eine Uhrzeit'), 'VALIDATION_ERROR');
      return;
    }

    try {
      setSubmitting(true);
      
      // Format the date
      const dateObj = new Date(formData.date);
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Generate the listing text
      let listing = `🍽️ Ich suche nach einer Begleitung bei ${selectedVenue.name} am ${formattedDate}`;
      
      if (formData.time) {
        listing += ` um ${formData.time}`;
      }
      
      listing += `!\n\n`;
      
      // Add venue details
      listing += `📍 ${selectedVenue.address || 'Location nach Anfrage verfügbar'}\n`;
      
      if (selectedVenue.cuisine_type) {
        listing += `🍲 Küche: ${selectedVenue.cuisine_type}\n`;
      } else if (selectedVenue.bar_type) {
        listing += `🍸 Bar Typ: ${selectedVenue.bar_type}\n`;
      }
      
      if (formData.partySize > 1) {
        listing += `👥 Suche ${formData.partySize - 1} mehr ${formData.partySize - 1 === 1 ? 'person' : 'people'}\n`;
      }
      
      if (formData.preferences) {
        listing += `\n✨ Präferenzen: ${formData.preferences}\n`;
      }
      
      if (formData.description) {
        listing += `\n${formData.description}\n`;
      }
      
      listing += `\nInteresse? Schick mir eine Nachricht!`;
      
      setGeneratedListing(listing);
    } catch (err) {
      console.error('Error generating listing:', err);
      setError(new Error('Failed to generate listing'), 'GENERATION_ERROR');
    } finally {
      setSubmitting(false);
    }
  };

  const saveListing = async () => {
    if (!selectedVenue || !formData.date || !formData.time) {
      setError(new Error('Bitte alle benötigten Felder ausfüllen'), 'VALIDATION_ERROR');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      // Prepare listing data
      const listingData = {
        restaurant_id: formData.venueType === 'restaurant' ? selectedVenue.id : undefined,
        bar_id: formData.venueType === 'bar' ? selectedVenue.id : undefined,
        date: formData.date,
        time: formData.time,
        party_size: formData.partySize,
        preferences: formData.preferences || undefined,
        description: formData.description || undefined,
        location_lat: selectedLocation?.lat,
        location_lng: selectedLocation?.lng
      };

      // Save the listing to the database
      await createListing(listingData);

      // Show success message
      setSuccessMessage('Deine Anfage wurde erfolgreich erstellt!');
      
      // Reset form after a delay
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (err) {
      console.error('Error saving listing:', err);
      setError(new Error('Failed to save listing'), 'SAVE_ERROR');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async () => {
    if (!generatedListing) return;
    
    try {
      await navigator.clipboard.writeText(generatedListing);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError(new Error('Failed to copy to clipboard'), 'COPY_ERROR');
    }
  };

  const shareViaChat = async (userId: string, userName: string) => {
    if (!generatedListing) return;
    
    try {
      setSubmitting(true);
      
      // Create or get chat room
      const chatRoomId = await createChatRoom([userId]);
      
      // Send the listing as a message
      await sendMessage(chatRoomId, generatedListing);
      
      // Navigate to the chat
      navigate(`/chat/${chatRoomId}`);
    } catch (err) {
      console.error('Error sharing via chat:', err);
      setError(new Error('Failed to share via chat'), 'SHARE_ERROR');
    } finally {
      setSubmitting(false);
      setShowShareModal(false);
    }
  };

  if (loading) {
    return <LoadingIndicator message={t('common.loading')} />;
  }

  if (isAuthenticated === false) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <User className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Anmeldung benötigt</h2>
          <p className="text-gray-600 mb-6">
            Du musst angemeldet sein um personalisierte Anfragen zu ersrtellen
          </p>
          <button 
            onClick={() => navigate('/login')}
            className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition"
          >
            Anmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <BackButton />
      
      {/* Hero Section */}
      <div className="relative mb-12 rounded-2xl overflow-hidden">
        <div 
          className="h-64 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 transform transition-all duration-500 hover:scale-105">
                Erstelle eine Dinner-Anfrage
              </h1>
              <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                Finde den optimalen Partner für ein Essen indem du eine personalisierte Anfrage erstellst
              </p>
            </div>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <Check className="h-5 w-5 mr-2" />
          <span>{successMessage}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Erstelle deine Anfrage</h2>
          
          {/* Venue Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schöne Essen oder auf ein Getränk?
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleInputChange({ target: { name: 'venueType', value: 'restaurant' } } as React.ChangeEvent<HTMLSelectElement>)}
                className={`py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition ${
                  formData.venueType === 'restaurant'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Restaurant className="h-5 w-5" />
                <span>Restaurant</span>
              </button>
              
              <button
                type="button"
                onClick={() => handleInputChange({ target: { name: 'venueType', value: 'bar' } } as React.ChangeEvent<HTMLSelectElement>)}
                className={`py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition ${
                  formData.venueType === 'bar'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Beer className="h-5 w-5" />
                <span>Bar</span>
              </button>
            </div>
          </div>
          
          {/* Venue Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {formData.venueType === 'restaurant' ? 'Restaurant' : 'Bar'}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={`Suche ein/e ${formData.venueType}...`}
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10 w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            
            {/* Venue Results */}
            <div className="mt-3 max-h-60 overflow-y-auto border rounded-lg">
              {filteredVenues.length > 0 ? (
                filteredVenues.map(venue => (
                  <div
                    key={venue.id}
                    onClick={() => handleVenueSelect(venue)}
                    className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                      selectedVenue?.id === venue.id ? 'bg-orange-50' : ''
                    }`}
                  >
                    <div className="flex justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{venue.name}</h3>
                        <div className="text-sm text-gray-500">
                          {venue.cuisine_type || venue.bar_type || 'No category'}
                          {venue.price_range && ` • ${venue.price_range}`}
                        </div>
                        {venue.address && (
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {venue.address}
                          </div>
                        )}
                      </div>
                      {selectedVenue?.id === venue.id && (
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  {searchQuery ? 'No venues found' : `Keine ${formData.venueType}s verfügbar`}
                </div>
              )}
            </div>
          </div>
          
          {/* Location */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location (Optional)
            </label>
            <LocationSearch
              onLocationSelect={handleLocationSelect}
              defaultValue={formData.location}
            />
            {selectedLocation && (
              <div className="mt-2 text-sm text-gray-500 flex items-center">
                <MapPin className="h-4 w-4 mr-1 text-orange-500" />
                <span>{selectedLocation.address}</span>
              </div>
            )}
          </div>
          
          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <DateInput
                label="Datum"
                value={formData.date}
                onChange={handleDateChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Uhrzeit
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Clock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  className="pl-10 w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                />
              </div>
            </div>
          </div>
          
          {/* Party Size */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gruppengröße
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Users className="h-5 w-5 text-gray-400" />
              </div>
              <select
                name="partySize"
                value={formData.partySize}
                onChange={handleInputChange}
                className="pl-10 w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                {[2, 3, 4, 5, 6, 7, 8].map(size => (
                  <option key={size} value={size}>
                    {size} {size === 1 ? 'person' : 'people'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Preferences */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wünsche (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 top-3 flex items-start pointer-events-none">
                <Utensils className="h-5 w-5 text-gray-400" />
              </div>
              <textarea
                name="preferences"
                value={formData.preferences}
                onChange={handleInputChange}
                placeholder="z.B. - Suche Foodies, Vegetarier willkommen, Bierliebhaber erwünscht usw"
                className="pl-10 w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                rows={2}
              />
            </div>
          </div>
          
          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Beschreibung (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Erzähle potentiellen Dinnerpartnern ein bisschen über dich und wonach du suchstr..."
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              rows={4}
            />
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={generateListing}
              disabled={submitting || !selectedVenue || !formData.date || !formData.time}
              className="flex-1 bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 transition flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  <span>Generiert...</span>
                </>
              ) : (
                <>
                  <MessageCircle className="h-5 w-5" />
                  <span>Vorschau</span>
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={saveListing}
              disabled={submitting || !selectedVenue || !formData.date || !formData.time}
              className="flex-1 bg-orange-500 text-white px-4 py-3 rounded-lg hover:bg-orange-600 transition flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  <span>Speichern...</span>
                </>
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  <span>Anfrage hochladen</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Preview Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Deine Anfragen</h2>
          
          {generatedListing ? (
            <>
              <div className="bg-gray-50 p-6 rounded-lg mb-6 whitespace-pre-wrap">
                {generatedListing}
              </div>
              
              <div className="flex flex-col space-y-4">
                <button
                  onClick={copyToClipboard}
                  className="bg-gray-100 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-200 transition flex items-center justify-center space-x-2"
                >
                  {copied ? (
                    <>
                      <Check className="h-5 w-5 text-green-500" />
                      <span>In Zwischenablage gespeichert!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-5 w-5" />
                      <span>In Zwischenablage kopieren</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => setShowShareModal(true)}
                  className="bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 transition flex items-center justify-center space-x-2"
                >
                  <Share2 className="h-5 w-5" />
                  <span>Im Chat teilen</span>
                </button>
              </div>
            </>
          ) : (
            <div className="bg-gray-50 p-6 rounded-lg h-64 flex items-center justify-center text-gray-400 text-center">
              <div>
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Deine Anfrage wird hier angezeigt sobald du sie erstellt hast!</p>
                <p className="text-sm mt-2">Fülle die Felder aus und klicke "Vorschau"</p>
              </div>
            </div>
          )}
          
          {/* Tips */}
          <div className="mt-8 bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-700 mb-2">Tipps für eine gelungene Anfrage!</h3>
            <ul className="text-sm text-blue-600 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Spare nicht an Tipps darüber wie dein Abendessen werden soll!</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Erwähne Wünsche, Vorlieben und Einschränkungen die du hast!</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Erzähle ein bisschen über dich um geeignete Leute zu finden</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Teile vielleicht auch Gesprächsthemen, die du dir wünschst!</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* User Search Modal for sharing */}
      <UserSearchModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onSelectUser={shareViaChat}
      />
    </div>
  );
};

export default CreateListing;